// user-state.service.ts
import {
  Injectable,
  signal,
  WritableSignal,
  computed,
  Signal,
} from '@angular/core';
import { ApiResponse } from '@core/interfaces/api/api-response.interface';
import { AdministratorDto } from '@core/dtos/administrator.dto';
import {
  ModulePermissionsDto,
  PermissionsDto,
  ProfileDto,
} from '@core/dtos/profile.dto';
import { Auth } from '@angular/fire/auth';
import { AuthFirebaseService } from '../auth-firebase/auth-firebase.service';
import { SideMenuItemDto } from '@core/dtos/side-menu-items.dto';

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private readonly _currentUser: WritableSignal<AdministratorDto | null> =
    signal(null);
  private readonly _currentProfile: WritableSignal<ProfileDto | null> =
    signal(null);
  private readonly _sideMenuItems: WritableSignal<SideMenuItemDto[] | null> =
    signal(null);

  public readonly currentUser: Signal<AdministratorDto | null> =
    this._currentUser.asReadonly();
  public readonly currentProfile: Signal<ProfileDto | null> =
    this._currentProfile.asReadonly();

  public readonly sideMenuItems: Signal<SideMenuItemDto[] | null> =
    this._sideMenuItems.asReadonly();

  public readonly isLoggedIn: Signal<boolean> = computed(
    () => !!this._currentUser()
  );

  // Claves para localStorage
  private readonly USER_STORAGE_KEY: string = 'user_state';
  private readonly PROFILE_STORAGE_KEY: string = 'profile_state';
  constructor(
    private authFirebaseService: AuthFirebaseService,
    private auth: Auth
  ) {
    this.auth.onAuthStateChanged(() => {
      this.loadFromStorage();
    });
  }

  /**
   * Carga la información del usuario desde la API
   */
  public loadUserInfo(): void {
    if (!this.auth.currentUser) return;
    this.authFirebaseService
      .getUserInfo()
      .subscribe((response: ApiResponse) => {
        this.updateUserInfo(response.data['administrator']);
        this.updateProfile(response.data['profile']);

        this.saveUserToStorage(response.data['administrator']);
        this.saveProfileToStorage(response.data['profile']);
      });
  }

  /**
   * Actualiza la información del usuario y la guarda en localStorage
   */
  public updateUserInfo(user: AdministratorDto): void {
    this._currentUser.set(user);
    this.saveUserToStorage(user);
  }

  public updateSideMenuItems(sideMenuItems: SideMenuItemDto[]): void {
    this._sideMenuItems.set(sideMenuItems);
  }

  /**
   * Actualiza el perfil y lo guarda en localStorage
   */
  public updateProfile(profile: ProfileDto): void {
    this._currentProfile.set(profile);
    this.saveProfileToStorage(profile);
  }

  /**
   * Limpia la información del usuario y del localStorage
   */
  public clearUserInfo(): void {
    this._currentUser.set(null);
    this.clearProfile();
    this.clearUserFromStorage();
    this.clearSideMenuItems();
  }

  /**
   * Limpia el perfil actual y del localStorage
   */
  public clearProfile(): void {
    this._currentProfile.set(null);
    this.clearProfileFromStorage();
  }

  public clearSideMenuItems(): void {
    this._sideMenuItems.set(null);
  }

  private getProfile(): ProfileDto | null {
    const profileStorage: string | null = localStorage.getItem(
      this.PROFILE_STORAGE_KEY
    );
    if (profileStorage) {
      return JSON.parse(profileStorage) as ProfileDto;
    }
    return null;
  }

  /**
   * Carga la información del usuario y perfil desde localStorage
   */
  private loadFromStorage(): void {
    const userStorage: string | null = localStorage.getItem(
      this.USER_STORAGE_KEY
    );
    const profileStorage: ProfileDto | null = this.getProfile();

    if (userStorage) {
      const user: AdministratorDto = JSON.parse(
        userStorage
      ) as AdministratorDto;
      this._currentUser.set(user);
    }

    if (profileStorage) {
      const profile: ProfileDto = profileStorage;
      this._currentProfile.set(profile);
    }

  // Si no hay datos en localStorage, cargar desde la API
  //  if (!userStorage || !profileStorage)
    this.loadUserInfo();
  }

  /**
   * Guarda el usuario en localStorage
   */
  private saveUserToStorage(user: AdministratorDto): void {
    try {
      localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Error al guardar usuario en localStorage:', error);
    }
  }

  /**
   * Guarda el perfil en localStorage
   */
  private saveProfileToStorage(profile: ProfileDto): void {
    try {
      localStorage.setItem(this.PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Error al guardar perfil en localStorage:', error);
    }
  }

  /**
   * Elimina el usuario del localStorage
   */
  private clearUserFromStorage(): void {
    try {
      localStorage.removeItem(this.USER_STORAGE_KEY);
    } catch (error) {
      console.error('Error al eliminar usuario del localStorage:', error);
    }
  }

  /**
   * Elimina el perfil del localStorage
   */
  private clearProfileFromStorage(): void {
    try {
      localStorage.removeItem(this.PROFILE_STORAGE_KEY);
    } catch (error) {
      console.error('Error al eliminar perfil del localStorage:', error);
    }
  }

  /**
   * Obtiene los permisos del usuario
   * @returns Un array de strings con los permisos del usuario
   */
  public getUserModulePermissions(): ModulePermissionsDto {
    const profile: ProfileDto | null =
      this.currentProfile() || this.getProfile();

    if (!profile || !profile.modulePermissions)
      return {} as ModulePermissionsDto;
    return profile.modulePermissions;
  }

  /**
   * Obtiene todos los permisos de un módulo específico
   * @param module - El nombre del módulo (ej: 'users', 'administrators', etc.)
   * @returns Un objeto con los permisos Create, Read, Update y Delete (todos false si no existen)
   */
  public getModulePermissions(module: string): PermissionsDto {
    const modulePermissions: ModulePermissionsDto =
      this.getUserModulePermissions();

    return {
      Create: modulePermissions[module]?.permissions.Create ?? false,
      Read: modulePermissions[module]?.permissions.Read ?? false,
      Update: modulePermissions[module]?.permissions.Update ?? false,
      Delete: modulePermissions[module]?.permissions.Delete ?? false,
    };
  }
}
